/**
 * 需求来源：/advertisingservice 页迁移为 React SSR 页（Egg.js + React SSR 同构框架）。
 * 注意事项：
 *  1. 忠实还原目标页 docs/.../advertisingservice.html 的 9 大 section（深色高端风），文案/图片/样式/动效 1:1。
 *     各 section 拆分为 components/*.jsx，样式全部迁移至 index.less（:global{} 保留字面量类名）。
 *  2. 本页无后端接口，fetch.js 仅解析 ossHost/fullName 并返回复用原页的 __seo。
 *  3. 联系交互复刻原 njk 的 Vue <workorder question-code="CJ003">：主 CTA(Get Started / Talk to an
 *     Account Specialist / Apply for an Ad Account)打开工单弹窗；Email 走 mailto、WhatsApp 走 wa.me 直链。
 *     全局 CJ_ / CJ_isLogin 仅客户端存在，所有访问置于事件回调 / useEffect 内并做 typeof window 防御。
 *  4. 不引入 Header/Footer（框架 Layout 自动注入）。路由由主流程统一注册（不改 ssrRoutes.js / router.js）。
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import { getConstants } from './constants';
import TicketContext from './components/TicketContext';
import ConstantsContext from './components/ConstantsContext';
import HeroSection from './components/HeroSection';
import PlatformMarquee from './components/PlatformMarquee';
import MultiPlatform from './components/MultiPlatform';
import RealBenefits from './components/RealBenefits';
import AccountSetup from './components/AccountSetup';
import ThreeStep from './components/ThreeStep';
import AudienceCards from './components/AudienceCards';
import Guarantee from './components/Guarantee';
import LightCta from './components/LightCta';
import WorkOrder from './WorkOrder';
import './index.less';

/**
 * 解析 URL query（仅客户端）。
 */
function getQuery(key) {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch (e) { return null; }
}

function AdvertisingService(props) {
  const pageData = props.pageData || {};
  // 取词一次；siteName(siteCheck.data('name')) 供固定文案 {{name}} 插值，fullName 预留 {{fullName}}
  const constants = useMemo(function () {
    return getConstants(pageData.fullName, pageData.siteName);
  }, [pageData.fullName, pageData.siteName]);
  const [ticketOpen, setTicketOpen] = useState(false);

  // 未登录跳登录页(target=base64(当前url带?show=ticket))
  const redirectToLogin = useCallback(function () {
    var url = window.location.href;
    var target = url.indexOf('show') > -1 ? url : url + '?show=ticket';
    var encoded = typeof window.btoa === 'function' ? window.btoa(target) : target;
    window.location.href = '/login.html?target=' + encoded;
  }, []);

  /**
   * 需求来源：260713 第8次补充 image-15 —— iPad 首次点击 CTA 无弹窗、需刷新（偶现）的时序修复。
   * 根因：原页工单是 CJ_ Vue 应用挂载的组件，点击时 window.CJ_isLogin 必已由同一应用就绪；迁 React SSR 后，
   *   React 独立 hydrate，用户点击时旧版 CJ_ 全局(负责设置 CJ_isLogin)可能尚未初始化 → 已登录用户被误判为未登录
   *   → 跳 login.html → 登录态存在再跳回(带 ?show=ticket) → 弹窗"刷新后才出现"。
   * 修复(仅改事件时序，不动 getQuestionFormByCode/createIssue 提交逻辑)：CJ_isLogin 未就绪(undefined)时不立即判定，
   *   轮询等待其就绪后再决策；已登录直接弹窗，确认未登录才跳登录。
   */
  const openTicket = useCallback(function () {
    if (typeof window === 'undefined') return;
    var tries = 0;
    var MAX_TRIES = 25; // 约 25 * 120ms ≈ 3s 兜底等待
    var attempt = function () {
      if (window.CJ_isLogin) { // 已登录：直接开弹窗
        setTicketOpen(true);
        return;
      }
      // CJ_isLogin 已就绪且为假 → 确认未登录，跳登录
      if (typeof window.CJ_isLogin !== 'undefined') {
        redirectToLogin();
        return;
      }
      // 全局登录态尚未就绪：稍后重试，避免误判
      if (tries < MAX_TRIES) {
        tries += 1;
        setTimeout(attempt, 120);
        return;
      }
      // 兜底：等待超时仍未就绪，沿用原逻辑跳登录
      redirectToLogin();
    };
    attempt();
  }, [redirectToLogin]);

  // 260714 第14次补充(需求㉑)：去掉 URL 的 ?show=ticket 参数（history.replaceState 就地替换当前历史条目）。
  //   场景：登录回跳带 ?show=ticket 自动开弹窗→提交跳成功页→浏览器返回该历史条目时 URL 仍带 show 会再次自动开弹窗。
  //   自动打开后与关闭时清掉 show，使该历史条目变干净，返回/刷新都不再自动弹出。
  const clearShowParam = useCallback(function () {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.has('show')) {
        u.searchParams.delete('show');
        var qs = u.searchParams.toString();
        window.history.replaceState(null, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
      }
    } catch (e) { /* noop */ }
  }, []);

  const closeTicket = useCallback(function () { setTicketOpen(false); clearShowParam(); }, [clearShowParam]);

  // 复刻 workorder.vue created()：url 带 show 且已登录时自动打开弹窗。
  // 260713 第8次补充 image-15：登录跳回(带 ?show=ticket)时 CJ_ 全局可能仍在初始化，登录态未就绪则轮询等待，
  // 避免自动弹窗丢失（与 openTicket 同一时序根因）。
  useEffect(function () {
    if (typeof window === 'undefined') return;
    if (!getQuery('show')) return;
    var tries = 0;
    var timer;
    var check = function () {
      // 260714 第14次补充(需求㉑)：自动开弹窗后立即清掉 URL 的 ?show=ticket，避免提交跳成功页后返回本页又自动弹出。
      if (window.CJ_isLogin) { setTicketOpen(true); clearShowParam(); return; }
      if (typeof window.CJ_isLogin !== 'undefined') return; // 就绪且未登录：不自动弹
      if (tries < 25) { tries += 1; timer = setTimeout(check, 120); }
    };
    check();
    return function () { if (timer) clearTimeout(timer); };
  }, [clearShowParam]);

  return (
    <ConstantsContext.Provider value={constants}>
      <TicketContext.Provider value={openTicket}>
        <div className="cj-advertising-service-page" data-gtag-module="advertising_service">
          <HeroSection />
          <PlatformMarquee />
          <MultiPlatform />
          <RealBenefits />
          <AccountSetup />
          <ThreeStep />
          <AudienceCards />
          <Guarantee />
          <LightCta />
          <WorkOrder open={ticketOpen} onClose={closeTicket} textConfig={constants.WORKORDER} />
        </div>
      </TicketContext.Provider>
    </ConstantsContext.Provider>
  );
}

AdvertisingService.fetch = fetchData;

export default withSSR(AdvertisingService);
